import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Scale, Brain, Zap, Target, ArrowRight, Dumbbell, Users, Heart, Apple, UserCheck, Trophy, Star, Shield } from "lucide-react";
import { uploadSrcSet, srcSetFallback } from "@/lib/imgVariants";
import axios from "axios";

const API = `/api`;

const iconMap = { Scale, Brain, Zap, Target, Dumbbell, Users, Heart, Apple, UserCheck, Trophy, Star, Shield };

const fallbackObjectives = [
  { id: "peso", icon: "Scale", title: "Perda de Peso e Confiança", hook: "A roupa já não serve como antes?", image: "https://images.unsplash.com/photo-1573879541250-58ae8b322b40?q=80&w=500&auto=format&fit=crop", content: "O excesso de peso não afeta apenas o corpo — afeta a autoestima, a disposição e até a forma como te apresentas ao mundo." },
  { id: "stress", icon: "Brain", title: "Menos Stress, Mais Equilíbrio", hook: "Cansaço mental constante?", image: "https://images.unsplash.com/photo-1541199249251-f713e6145474?q=80&w=500&auto=format&fit=crop", content: "O stress do dia a dia está a roubar-te energia e qualidade de vida. O treino é o teu escape." },
  { id: "energia", icon: "Zap", title: "Mais Energia para Viver", hook: "Acordas já cansado?", image: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=500&auto=format&fit=crop", content: "A falta de energia não é normal — é um sinal de que o teu corpo precisa de movimento." },
  { id: "disciplina", icon: "Target", title: "Disciplina que Muda Tudo", hook: "Prometes começar 'segunda-feira'?", image: "https://images.unsplash.com/photo-1552674605-db6ffd4facb5?q=80&w=500&auto=format&fit=crop", content: "Com orientação e compromisso, deixas de depender da motivação e passas a criar resultados reais." },
];

function ObjectiveCard({ item, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  const IconComponent = iconMap[item.icon] || Target;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.15, ease: "easeOut" }}
      data-testid={`objective-card-${item.id}`}
    >
      <div className="group relative h-full bg-[#1F1F1F] rounded-2xl overflow-hidden border border-white/[0.06] hover:border-[#FED601]/40 transition-all duration-500 shadow-lg hover:shadow-[0_8px_40px_rgba(234,179,8,0.12)]">
        <div className="relative h-[200px] sm:h-[220px] overflow-hidden">
          <img src={item.image} srcSet={uploadSrcSet(item.image)} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw" onError={srcSetFallback} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" decoding="async" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1F1F1F] via-[#1F1F1F]/40 to-transparent" />
        </div>
        <div className="p-5 sm:p-6">
          <p className="text-[#FED601] text-sm font-semibold uppercase tracking-wider mb-1.5 line-clamp-1">{item.hook}</p>
          <h3 className="text-lg sm:text-xl font-bold text-white mb-2 leading-tight line-clamp-2 min-h-[3.5rem] sm:min-h-[3.75rem]">{item.title}</h3>
          <p className="text-gray-400 text-sm leading-relaxed line-clamp-4 min-h-[5rem]">{item.content}</p>
        </div>
      </div>
    </motion.div>
  );
}

export const Objectives = () => {
  const [objectives, setObjectives] = useState(fallbackObjectives);
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true, margin: "-60px" });

  useEffect(() => {
    axios.get(`${API}/content/objectives`)
      .then((res) => {
        if (res.data && res.data.length > 0) setObjectives(res.data);
      })
      .catch(() => {});
  }, []);

  const scrollToContact = () => {
    const el = document.querySelector("#contacto");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="py-16 lg:py-24 bg-[#FAF8F5] overflow-hidden" data-testid="objectives-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div ref={headerRef} initial={{ opacity: 0, y: 30 }} animate={headerInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }} className="text-center mb-14 lg:mb-16">
          <span className="text-[#FED601] text-sm font-bold uppercase tracking-widest">Identifica-te</span>
          <h2 className="text-4xl sm:text-5xl font-black text-[#1A1A1A] mt-2 mb-4">Qual é o <span className="text-[#FED601]">Teu Desafio</span>?</h2>
          <p className="text-[#4A4A4A] max-w-md mx-auto text-sm">Escolhe o desafio que mais te representa e dá o primeiro passo.</p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {objectives.map((item, index) => (
            <ObjectiveCard key={item.id} item={item} index={index} />
          ))}
        </div>

        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="text-center mt-14 lg:mt-16">
          <p className="text-[#4A4A4A] mb-6">Seja qual for o teu desafio, temos a solução. <span className="text-[#1A1A1A] font-bold">Aceitas?</span></p>
          <button onClick={scrollToContact} className="btn-primary px-10 py-4 text-lg" data-testid="objectives-main-cta">Aceito o Desafio!</button>
        </motion.div>
      </div>
    </section>
  );
};
